import { supabase } from '@/lib/supabase';
import type { BannerType } from '@/config/banners';

export interface BannerDismissalRow {
  banner_type: BannerType;
  dismissed_at: string;
  snoozed_until: string | null;
}

export async function listDismissals(): Promise<BannerDismissalRow[]> {
  const { data, error } = await supabase
    .from('banner_dismissals')
    .select('banner_type, dismissed_at, snoozed_until');
  if (error) throw error;
  return (data ?? []) as BannerDismissalRow[];
}

export interface UpsertDismissalInput {
  bannerType: BannerType;
  userId: string;
  /** If undefined, dismissal is permanent (snoozed_until=null). */
  snoozeDays?: number;
}

export async function upsertDismissal(
  input: UpsertDismissalInput,
): Promise<void> {
  const snoozed_until =
    input.snoozeDays !== undefined
      ? new Date(Date.now() + input.snoozeDays * 24 * 3600 * 1000).toISOString()
      : null;
  // dismissed_at is intentionally the CLIENT clock. The column does have a
  // server-side `default now()` (see 20260428000003_create_banner_dismissals),
  // but that default only applies on INSERT — this is an upsert, and on the
  // re-dismiss/ON CONFLICT UPDATE path a column omitted from the payload
  // keeps its EXISTING value rather than refreshing, which would freeze
  // dismissed_at at the first-ever dismiss and break re-dismissing a banner
  // (e.g. digest_delivery_failed) a second time. Stamping it server-side on
  // every write would need a trigger/RPC (migration) instead; until that
  // exists, the client clock is the pragmatic choice, and the resulting
  // client/server clock-skew is absorbed with a grace window where this
  // timestamp is compared against server timestamps — see
  // CLOCK_SKEW_GRACE_MS in useSystemBanners.ts.
  const { error } = await supabase
    .from('banner_dismissals')
    .upsert(
      {
        user_id: input.userId,
        banner_type: input.bannerType,
        dismissed_at: new Date().toISOString(),
        snoozed_until,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,banner_type' },
    )
    .select();
  if (error) throw error;
}

export interface RemoveDismissalInput {
  bannerType: BannerType;
  userId: string;
}

export async function removeDismissal(
  input: RemoveDismissalInput,
): Promise<void> {
  const { error } = await supabase
    .from('banner_dismissals')
    .delete()
    .eq('user_id', input.userId)
    .eq('banner_type', input.bannerType);
  if (error) throw error;
}
